/**
 * 계정의 장수 기술(Todo10 3단계, `sim/skills.ts`): 들고 나갈 기술 하나와 기술마다의 레벨. 장수 레벨 10마다 기술 점수
 * 1점(`skillPointsFor`) — 모든 기술은 레벨 1에서 시작하고, 점수를 넣어 2~5로 올린다. 되돌리면 냥이 든다(`SKILL_RESET_GOLD`).
 */
import { SKILL_BY_ID, SKILL_LEVEL_MAX, SKILL_ORDER, Skill, skillPointsFor } from '../sim/skills.ts';
import type { Army } from './army.ts';
import { heroLevelOf } from './army.ts';

export interface SkillState {
  pick: Skill;
  /** 기술마다 레벨. 없으면 1. */
  levels: Partial<Record<Skill, number>>;
}

/** 기술 점수를 모두 되돌리는 값(냥). 고르기를 가볍게 바꿀 수 있되 공짜는 아니게. */
export const SKILL_RESET_GOLD = 300;

export function skillStateOf(army: Army): SkillState {
  if (!army.skills) army.skills = { pick: Skill.Roar, levels: {} };
  return army.skills;
}

export function skillLevelOf(army: Army, id: Skill): number {
  return army.skills?.levels[id] ?? 1;
}

/** 넣은 점수(레벨 1 위로 올린 몫의 합). */
export function spentSkillPoints(army: Army): number {
  return SKILL_ORDER.reduce((n, id) => n + skillLevelOf(army, id) - 1, 0);
}

/** 남은 기술 점수. */
export function freeSkillPoints(army: Army): number {
  return skillPointsFor(heroLevelOf(army)) - spentSkillPoints(army);
}

export function pickSkill(army: Army, id: Skill): boolean {
  if (!SKILL_BY_ID.has(id)) return false;
  skillStateOf(army).pick = id;
  return true;
}

/** 점수 하나를 넣어 한 레벨 올린다. 점수가 없거나 끝이면 false. */
export function raiseSkill(army: Army, id: Skill): boolean {
  if (!SKILL_BY_ID.has(id) || freeSkillPoints(army) <= 0) return false;
  const lv = skillLevelOf(army, id);
  if (lv >= SKILL_LEVEL_MAX) return false;
  skillStateOf(army).levels[id] = lv + 1;
  return true;
}

/** 넣은 점수를 모두 되돌린다(냥이 든다). 넣은 것이 없거나 냥이 모자라면 false. */
export function resetSkills(army: Army): boolean {
  if (spentSkillPoints(army) === 0 || army.gold < SKILL_RESET_GOLD) return false;
  army.gold -= SKILL_RESET_GOLD;
  skillStateOf(army).levels = {};
  return true;
}

/** 이 판에 들고 나가는 기술(명부, `ScenarioConfig.skill`). */
export function skillForBattle(army: Army): { id: Skill; level: number } {
  const pick = army.skills?.pick ?? Skill.Roar;
  return { id: pick, level: skillLevelOf(army, pick) };
}

/**
 * 저장에서 돌아온 기술을 거른다. 모르는 기술은 함성, 레벨은 1~5 정수. 넣은 점수가 받은 점수보다 많으면(손으로 고친 저장)
 * 모두 되돌린다 — 어느 것을 깎을지 고르는 것보다 정직하다.
 */
export function normalizeSkills(army: Army): void {
  const raw = (army as { skills?: unknown }).skills;
  if (!raw || typeof raw !== 'object') {
    delete army.skills;
    return;
  }
  const r = raw as { pick?: unknown; levels?: unknown };
  const pick = typeof r.pick === 'string' && SKILL_BY_ID.has(r.pick) ? (r.pick as Skill) : Skill.Roar;
  const levels: Partial<Record<Skill, number>> = {};
  const src = r.levels && typeof r.levels === 'object' ? (r.levels as Record<string, unknown>) : {};
  for (const id of SKILL_ORDER) {
    const v = src[id];
    if (typeof v === 'number' && Number.isInteger(v) && v > 1) levels[id] = Math.min(SKILL_LEVEL_MAX, v);
  }
  army.skills = { pick, levels };
  if (freeSkillPoints(army) < 0) army.skills.levels = {};
}
